import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    IconButton,
    CircularProgress
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { DpeComplet } from '../interfaces/DpeComplet';
import { putDpeFile } from '../services/dpeService';

interface DpeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    dpe?: DpeComplet | null;
    refreshDpes: () => Promise<void>; 
}

const DpeModal: React.FC<DpeModalProps> = ({ isOpen, onClose, onSave, dpe, refreshDpes }) => {
    const [numeroDpe, setNumeroDpe] = useState(dpe?.dpe.numeroDpe || '');
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const selectedFile = acceptedFiles[0];
        if (selectedFile?.type !== 'text/xml' && !selectedFile?.name.endsWith('.xml')) {
            setError('Seuls les fichiers XML sont acceptés');
            return;
        }
        setFile(selectedFile);
        setError(null);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/xml': ['.xml']
        },
        maxFiles: 1
    });

    const handleSubmit = async () => {
        if (!file) {
            setError('Veuillez sélectionner un fichier XML');
            return;
        }
        if (!numeroDpe.trim()) {
            setError('Veuillez saisir le numéro DPE');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            await putDpeFile(numeroDpe,file);
            await refreshDpes();
            onClose();
            setNumeroDpe('');
            setFile(null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Une erreur est survenue lors de l\'import');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        setError(null);
    };

    useEffect(() => {
        if (isOpen) {
            setNumeroDpe('');
            setFile(null);
            setError(null);
        }
    }, [isOpen]);

    return (
        <Dialog 
            open={isOpen} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            className="backdrop-blur-sm"
        >
            <DialogTitle className="flex justify-between items-center bg-emerald-50">
                <Typography variant="h6" className="text-emerald-800 font-bold">
                    {dpe ? 'Modifier le DPE' : 'Ajouter un nouveau DPE'}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className="mt-4">
                <div className="space-y-6 mt-4">
                    <TextField
                        fullWidth
                        label="Numéro DPE"
                        value={numeroDpe}
                        onChange={(e) => setNumeroDpe(e.target.value)}
                        variant="outlined"
                        disabled={loading}
                        error={!!error && !numeroDpe.trim()}
                        helperText={error && !numeroDpe.trim() ? 'Ce champ est requis' : ''}
                    />

                    <div
                        {...getRootProps()}
                        className={`
                            border-2 border-dashed rounded-lg p-6
                            transition-colors duration-200 ease-in-out
                            ${isDragActive ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300'}
                            ${error ? 'border-red-500 bg-red-50' : ''}
                            hover:border-emerald-500 hover:bg-emerald-50
                            cursor-pointer
                        `}
                    >
                        <input {...getInputProps()} />
                        <div className="text-center space-y-4">
                            <UploadFileIcon className={`
                                text-5xl mx-auto
                                ${isDragActive ? 'text-emerald-500' : 'text-gray-400'}
                            `} />
                            {file ? (
                                <div className="flex items-center justify-center gap-2">
                                    <Typography className="text-emerald-600">
                                        {file.name}
                                    </Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveFile();
                                        }}
                                        className="text-red-500"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            ) : (
                                <>
                                    <Typography className="text-gray-600">
                                        Glissez et déposez votre fichier XML ici, ou cliquez pour sélectionner
                                    </Typography>
                                    <Typography className="text-sm text-gray-500">
                                        Format accepté : XML uniquement
                                    </Typography>
                                </>
                            )}
                        </div>
                    </div>

                    {error && !file && (
                        <Typography className="text-red-500 text-sm">
                            {error}
                        </Typography>
                    )}
                </div>
            </DialogContent>

            <DialogActions className="p-4 bg-gray-50">
                <Button
                    onClick={onClose}
                    disabled={loading}
                    className="text-gray-600"
                >
                    Annuler
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={loading || !file || !numeroDpe.trim()}
                    variant="contained"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DpeModal;